import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy,
  onSnapshot 
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'customers';

// Get all customers
export const getCustomers = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const customers = [];
    querySnapshot.forEach((doc) => {
      customers.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: customers };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Listen to customers changes in real-time
export const subscribeToCustomers = (callback) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const customers = [];
    snapshot.forEach((doc) => {
      customers.push({ id: doc.id, ...doc.data() });
    });
    callback(customers);
  });
};

// Add new customer
export const addCustomer = async (customerData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...customerData,
      totalOrders: 0,
      totalSpent: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update customer
export const updateCustomer = async (id, customerData) => {
  try {
    const customerRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(customerRef, {
      ...customerData,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete customer
export const deleteCustomer = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update customer stats (orders and spending)
export const updateCustomerStats = async (id, totalOrders, totalSpent) => {
  try {
    const customerRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(customerRef, {
      totalOrders,
      totalSpent,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Recalculate customer stats from actual bills
export const recalculateCustomerStats = async (customerId, customerName) => {
  try {
    // Get all bills for this customer
    const billsQuery = query(
      collection(db, 'bills'),
      orderBy('createdAt', 'desc')
    );
    const billsSnapshot = await getDocs(billsQuery);
    
    let totalOrders = 0;
    let totalSpent = 0;
    
    // Count bills and sum totals for this customer
    billsSnapshot.forEach((doc) => {
      const bill = doc.data();
      // Match by customer name (since bills store customer name, not ID)
      if (bill.customer === customerName || bill.customerId === customerId) {
        totalOrders += 1;
        totalSpent += (bill.total || 0);
      }
    });
    
    // Update customer with calculated stats
    const customerRef = doc(db, COLLECTION_NAME, customerId);
    await updateDoc(customerRef, {
      totalOrders,
      totalSpent,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true, totalOrders, totalSpent };
  } catch (error) {
    console.error('Error recalculating customer stats:', error);
    return { success: false, error: error.message };
  }
};

// Recalculate all customer stats from bills
export const recalculateAllCustomerStats = async () => {
  try {
    // Get all customers
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    
    // Get all bills once
    const billsSnapshot = await getDocs(collection(db, 'bills'));
    const allBills = [];
    billsSnapshot.forEach((doc) => {
      allBills.push({ id: doc.id, ...doc.data() });
    });
    
    const updates = [];
    
    // For each customer, calculate stats from bills
    customersSnapshot.forEach((customerDoc) => {
      const customer = { id: customerDoc.id, ...customerDoc.data() };
      
      // Count bills for this customer
      let totalOrders = 0;
      let totalSpent = 0;
      
      allBills.forEach((bill) => {
        if (bill.customer === customer.name || bill.customerId === customer.id) {
          totalOrders += 1;
          totalSpent += (bill.total || 0);
        }
      });
      
      // Only update if values changed
      if (customer.totalOrders !== totalOrders || customer.totalSpent !== totalSpent) {
        updates.push(
          updateDoc(doc(db, COLLECTION_NAME, customer.id), {
            totalOrders,
            totalSpent,
            updatedAt: new Date().toISOString()
          })
        );
      }
    });
    
    // Execute all updates
    await Promise.all(updates);
    
    return { success: true, updatedCount: updates.length };
  } catch (error) {
    console.error('Error recalculating all customer stats:', error);
    return { success: false, error: error.message };
  }
};
